import {Component, EventEmitter, OnInit, Output, signal} from '@angular/core';
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {HttpErrorResponse, HttpStatusCode} from "@angular/common/http";
import {ActivatedRoute, Router} from "@angular/router";
import {UtilService, CustomMessageService, ResponseHttpSuccess, LocalStorageService, OriginMessageError} from "library";
import {CategoryRequestService} from "projects/store/src/app/services/api/v1/category-request.service";
import {PathNavigationService} from 'projects/store/src/app/services/api/v1/path-navigation.service';
import {UserValidatorService} from "projects/store/src/app/services/api/v1/user-validator.service";
import {RequestService} from "projects/store/src/app/services/api/v1/request.service";
import {environment} from "projects/store/src/environments/environment";
import {LocalStorageKey, RequestCancelReason} from "projects/store/src/app/enums";
import {AutoCompleteCompleteEvent, AutoCompleteOnSelectEvent} from "primeng/autocomplete";
import {SalesWorkflowService} from "projects/store/src/app/services/api/v1/sales-workflow.service";
import {AuthService} from "../../../../services/api/v1/auth.service";
import {PixelService} from "projects/store/src/app/services/api/v1/pixel.service";

enum Steps {
  'form' = 'form',
  'message' = 'message',
}

@Component({
  selector: 'app-sales-client-identification-kredicash',
  templateUrl: './sales-client-identification-kredicash.component.html'
})
export class SalesClientIdentificationKredicashComponent implements OnInit {
  formRegister!: FormGroup;
  documentTypeList: {label: string, value: string}[] = environment.global.country.documentTypeOptions;
  country = environment.global.country;
  dataResults = signal<any>({});
  isSubmitted = signal<boolean>(false);
  isLoading = signal<boolean>(false);
  step = Steps.form;
  requestId!: string
  maxLengthCharacterFromDocument = 20;
  maxLengthCharacterFromName = 40;
  maxLengthCharacterFromLastname = 40;
  filteredDomains: string[] = [];
  commonDomains = ['gmail.com', 'hotmail.com', 'outlook.com', 'yahoo.com', 'icloud.com'];
  flowCode: string;
  @Output() nextStepEvent = new EventEmitter();
  @Output() gotoStepEvent = new EventEmitter()

  constructor(private router: Router,
              private categoryRequestService: CategoryRequestService,
              private utilService: UtilService,
              private route: ActivatedRoute,
              private customMessageService: CustomMessageService,
              private userValidatorService: UserValidatorService,
              private requestService: RequestService,
              private salesWorkflowService: SalesWorkflowService,
              private localStorageService: LocalStorageService,
              private pathNavigationService: PathNavigationService,
              private metaPixelService: PixelService,
              private authService: AuthService,) {
    this.utilService.disablePageLoading();
    this.requestId = this.route.snapshot.params['request_id'];
    this.flowCode = this.route.snapshot.params['flow_code'];

    this.formRegister = new FormGroup({
      document_id_number: new FormControl('', Validators.required),
      document_id_type: new FormControl('', Validators.required),
      email: new FormControl('', {
        validators: [Validators.required],
        asyncValidators: [this.userValidatorService.emailServiceStatusValid(), this.userValidatorService.emailServiceExistValidator()],
        updateOn: 'blur'
      }),
      first_name: new FormControl('', Validators.required),
      last_name: new FormControl('', Validators.required),
      phone: new FormControl('', {
        validators: [Validators.required, Validators.minLength(environment.global.country.minPhoneNumberCharacters),
          Validators.maxLength(environment.global.country.maxPhoneNumberCharacters)],
        asyncValidators: [this.userValidatorService.phoneServiceExistValidator(this.requestId), this.userValidatorService.phoneServiceVerification()],
        updateOn: 'blur'
      }),
      bureau_authorization: new FormControl(null, Validators.requiredTrue),
      personal_data_authorization: new FormControl(null, Validators.requiredTrue),
    });
    this.requestService.step.set(2);
  }

  ngOnInit() {
    // Load simulation data from query params if available (from iframe)
    // Use observable instead of snapshot to handle window.top.location.href navigation
    this.route.queryParams.subscribe(params => {
      this.loadSimulationDataFromQueryParams(params);
    });
  }

  private loadSimulationDataFromQueryParams(params: any) {
    const simulationDataParam = params['simulation_data'];
    const utmDataParam = params['utm_data'];
    const startTokenParam = params['start_token'];

    console.log('🔍 Query params recibidos:', {
      hasSimulation: !!simulationDataParam,
      hasUtm: !!utmDataParam,
      hasStartToken: !!startTokenParam
    });

    // Decode and store simulation data
    if (simulationDataParam) {
      try {
        const decodedSimulation = JSON.parse(atob(decodeURIComponent(simulationDataParam)));
        this.localStorageService.set(LocalStorageKey.simulation, decodedSimulation);
        console.log('✅ Simulation data loaded from query params:', decodedSimulation);
      } catch (error) {
        this.customMessageService.logErrorOnly({ message: 'Error decoding simulation data:', error: error, simulationDataParam}, OriginMessageError.FRONTEND);
      }
    } else {
      console.log('ℹ️ No simulation_data param found');
    }

    // Decode and store UTM data
    if (utmDataParam) {
      try {
        const decodedUtm = JSON.parse(atob(decodeURIComponent(utmDataParam)));
        this.localStorageService.set(LocalStorageKey.utmData, decodedUtm);
        console.log('✅ UTM data loaded from query params:', decodedUtm);
      } catch (error) {
        this.customMessageService.logErrorOnly({ message: 'Error decoding UTM data:', error: error, utmDataParam}, OriginMessageError.FRONTEND);
      }
    } else {
      console.log('ℹ️ No utm_data param found');
    }

    // Store start_token directly (it's already a string, not encoded)
    if (startTokenParam) {
      try {
        // Decode the base64 token to get the object
        const decodedToken = JSON.parse(atob(startTokenParam));
        this.localStorageService.set(LocalStorageKey.startToken, decodedToken);
        console.log('✅ Start token loaded from query params:', decodedToken);
      } catch (error) {
        this.customMessageService.logErrorOnly({ message: 'Error decoding start_token:', error: error, startTokenParam}, OriginMessageError.FRONTEND);
      }
    } else {
      console.log('ℹ️ No start_token param found');
    }
  }

  onRegister(){
    this.metaPixelService.trackEvent('CompleteRegistration');
    this.formRegister.value.phone = this.formRegister.value.phone.replace(environment.global.country.phoneCountryCode, '').replace(/[^0-9 ]/g, '').replace(/ /g,'');
    this.isLoading.set(true);
    this.isSubmitted.set(true);
    if (this.formRegister.invalid){
      this.isLoading.set(false);
      return;
    }
    this.categoryRequestService.createCategoryRequest(this.requestService.categoryId, this.formRegister.value).subscribe({
      next: (resp: ResponseHttpSuccess)=> {
        if (resp.status === HttpStatusCode.Created){
          if(resp.body.data.approved == false) {
            this.cancelRequest(resp.body.data.request_id)
          }else{
            this.dataResults.set(resp.body.data);
            this.step = Steps.message;
            this.updateRequest();
            this.onNextStep();
          }
        }else{
          this.customMessageService.responseNoticeHandler(resp);
        }
        this.isLoading.set(false);
      },
      error: (err: HttpErrorResponse) => {
        this.customMessageService.responseNoticeHandler(err);
        this.isLoading.set(false);
      }
    });
  }

  onDomainFilter(event: AutoCompleteCompleteEvent) {
    const query = event.query;

    if (query.includes('@')) {
      const [prefix, partialDomain] = query.split('@');
      this.filteredDomains = this.commonDomains
        .filter((domain: string) => domain.startsWith(partialDomain.toLowerCase()))
        .map((domain: any) => `${prefix}@${domain}`);
    } else {
      this.filteredDomains = [];
    }
  }

  onDomainSelect(event: AutoCompleteOnSelectEvent) {
    this.formRegister.patchValue({email: event.value});
  }

  updateRequest() {
    let data: any = { simulation: this.localStorageService.get(LocalStorageKey.simulation)}

    const utmData = this.localStorageService.get(LocalStorageKey.utmData);
    if (utmData) {
      data.utm_data = utmData;
    }

    this.requestService.update(this.dataResults().request_id, data).subscribe({
      next: (resp: ResponseHttpSuccess)=> {
        if (resp.status == HttpStatusCode.NoContent){
          this.localStorageService.remove(LocalStorageKey.simulation)
          this.localStorageService.remove(LocalStorageKey.utmData)
        }else{
          this.customMessageService.responseNoticeHandler(resp);
        }
      },
      error: (err: HttpErrorResponse) => {
        if ( err.status === HttpStatusCode.Locked) {
          this.handleRequestLocked(err as any);
        }else{
          this.customMessageService.responseNoticeHandler(err);
        }
      }
    });
  }

  isDisabled(){
    return this.formRegister.invalid || this.formRegister.pending;
  }

  cancelRequest(requestId: string) {
    const reasonText = RequestCancelReason.REQUEST_NOT_APPROVED;
    this.metaPixelService.trackEvent('RejectedApplication');
    this.requestService.cancel(requestId, reasonText).subscribe({
      next: (resp: ResponseHttpSuccess)=> {
        if(resp.status === HttpStatusCode.NoContent) {
          this.handleRequestNotApproved(reasonText);
        }else{
          this.customMessageService.responseNoticeHandler(resp);
        }
      },
      error: (err: HttpErrorResponse)=>{
        this.customMessageService.responseNoticeHandler(err);
      }
    })
  }

  get fr(){
    return this.formRegister.controls;
  }

  onNextStep() {
    let data = {
      requestId: this.dataResults().request_id,
      queryParams: {resume: this.dataResults().resume}
    }
    this.nextStepEvent.emit(data);
  }

  onGotoStep(stepCode: string) {
    this.gotoStepEvent.emit(stepCode);
  }

  private handleRequestLocked(resp: any): void {
    const messageError = resp.error?.message ?? 'Solicitud no disponible';
    const attemptedUrl = this.utilService.getCurrentUrl();
    const state = { attemptedUrl, messageError };

    this.pathNavigationService.navigateToRequestNotAvailable(this.flowCode, state);
  }

  private handleRequestNotApproved(messageError: string): void {
    const attemptedUrl = this.utilService.getCurrentUrl();
    const state = { attemptedUrl, messageError };

    this.pathNavigationService.navigateToRequestNotApproved(this.flowCode, state);
  }

  protected readonly Steps = Steps;
}
